import { SERVER_URL } from '../config';

/* Minimal fetch wrapper. Deliberately dependency-free (no axios) so this folder
   drops into any host without version conflicts. It speaks the same shape the
   TeamGrid API already returns, and stores auth exactly like the host's
   AuthContext does, so a session created here is valid in the dashboard. */

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/* The API reports validation failures in several shapes depending on which
   layer rejected the request:
     express-validator  -> { error: { details: [ { msg } ] } }  or  { errors: [...] }
     hand-rolled forms  -> { details: { fieldName: 'message', ... } }   (contact.js)
     everything else    -> { message } / { error: { message } }
   Surfacing only "Validation failed" would leave the user with no idea which
   field to fix, so pull the specific messages out when they exist. */
function pickMessage(data, fallback) {
  const detailObj = data?.details ?? data?.error?.details;
  if (detailObj && typeof detailObj === 'object') {
    const parts = Array.isArray(detailObj)
      ? detailObj.map((d) => (typeof d === 'string' ? d : d?.msg || d?.message)).filter(Boolean)
      : Object.values(detailObj).filter((d) => typeof d === 'string');
    if (parts.length) return parts.join(' ');
  }
  if (Array.isArray(data?.errors) && data.errors.length) {
    const parts = data.errors.map((e) => e?.msg || e?.message).filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  return (
    data?.error?.message ||
    data?.message ||
    (typeof data?.error === 'string' ? data.error : null) ||
    data?.data?.message ||
    fallback
  );
}

/** The token the host's AuthContext keeps. Needed by authenticated endpoints. */
export function getToken() {
  try {
    return localStorage.getItem('token') || null;
  } catch {
    return null;
  }
}

function buildHeaders(auth) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function request(method, path, body, { signal, auth = false } = {}) {
  let res;
  try {
    res = await fetch(`${SERVER_URL}${path}`, {
      method,
      headers: buildHeaders(auth),
      ...(body === undefined ? {} : { body: JSON.stringify(body ?? {}) }),
      signal,
    });
  } catch {
    throw new ApiError('Network error. Check your connection and try again.', 0, null);
  }

  /* If SERVER_URL is misconfigured the request hits the web server instead of
     the API and comes back as 200 text/html. Without this check that parsed to
     `null`, passed the res.ok test, and sailed on as a SUCCESS — sign-up would
     show the plan step for an account that was never created. Fail loudly. */
  const contentType = res.headers.get('content-type') || '';
  if (res.ok && contentType.includes('text/html')) {
    throw new ApiError(
      'The server returned a web page instead of data. The API address looks misconfigured.',
      res.status,
      null,
    );
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* some endpoints legitimately return an empty body on success */
  }

  if (!res.ok) {
    throw new ApiError(
      pickMessage(
        data,
        res.status === 429
          ? 'Too many attempts. Please wait and try again.'
          : 'Something went wrong. Please try again.',
      ),
      res.status,
      data,
    );
  }

  /* The API answers 200 with { success: false, message } on some validation
     paths (contact.js does this), so a 2xx alone is not proof of success. */
  if (data && data.success === false) {
    throw new ApiError(pickMessage(data, 'Something went wrong. Please try again.'), res.status, data);
  }
  return data;
}

export const apiPost = (path, body, opts) => request('POST', path, body, opts);
export const apiGet = (path, opts) => request('GET', path, undefined, opts);

/** Persist a session in the exact shape the host's AuthContext expects. */
export function storeSession(payload) {
  const d = payload?.data ?? payload ?? {};
  const token = d.token ?? d.accessToken;
  if (!token) return false;
  try {
    localStorage.setItem('token', token);
    if (d.refreshToken) localStorage.setItem('refreshToken', d.refreshToken);
    if (d.user) localStorage.setItem('user', JSON.stringify(d.user));
    if (d.organization) localStorage.setItem('organization', JSON.stringify(d.organization));
  } catch {
    return false;
  }
  /* The host's CRM module listens for this to pick up a token in the same tab.
     Landing auth pages hand off with a full page load, so the host re-reads
     localStorage anyway — this just keeps parity with AuthContext.login. */
  try {
    window.dispatchEvent(new Event('auth-token-changed'));
  } catch {
    /* older browsers / non-DOM env */
  }
  return true;
}
